import { useId, useState, type FormEvent } from 'react'
import { ThumbsDown } from 'lucide-react'
import type { FeedbackReason } from '@/api/types'
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'

const REASONS: { value: FeedbackReason; label: string }[] = [
  { value: 'wrong', label: 'Wrong' },
  { value: 'outdated', label: 'Outdated' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'wrong_source', label: 'Wrong source' },
  { value: 'other', label: 'Other' },
]

type Props = {
  active: boolean
  disabled?: boolean
  onSubmit: (reason: FeedbackReason | undefined, comment: string) => Promise<boolean>
}

/** Thumbs-down with a reason picker and optional comment. */
export function FeedbackPopover({ active, disabled, onSubmit }: Props) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<FeedbackReason | undefined>(undefined)
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const titleId = useId()

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setFailed(false)
    const ok = await onSubmit(reason, comment.trim())
    setBusy(false)
    if (ok) {
      setOpen(false)
      setComment('')
    } else {
      setFailed(true)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <IconButton label="Bad answer" aria-pressed={active} active={active} disabled={disabled} size="xs">
          <ThumbsDown className={cn(active && 'fill-current')} />
        </IconButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(20rem,calc(100vw-2rem))]">
        <form onSubmit={submit}>
          <p className="font-medium text-ink" id={titleId}>
            What was wrong with this answer?
          </p>
          <div role="radiogroup" aria-labelledby={titleId} className="mt-3 flex flex-wrap gap-1.5">
            {REASONS.map((r) => {
              const checked = reason === r.value
              return (
                <button
                  key={r.value}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  onClick={() => setReason(checked ? undefined : r.value)}
                  className={cn(
                    'h-7 rounded-full border px-3 text-xs font-medium transition-colors',
                    checked
                      ? 'border-accent bg-accent-soft text-accent-text'
                      : 'border-line text-ink-muted hover:border-line-strong hover:text-ink',
                  )}
                >
                  {r.label}
                </button>
              )
            })}
          </div>
          <label className="mt-3 block text-xs font-medium text-ink-muted">
            Comment <span className="font-normal text-ink-faint">(optional)</span>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="What should the answer have said?"
              className="mt-1 block w-full resize-none rounded-lg border border-line bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-accent focus:outline-none"
            />
          </label>
          {failed && (
            <p className="mt-2 text-xs text-danger" role="alert">
              Couldn&rsquo;t send feedback. Please try again.
            </p>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" type="submit" disabled={busy}>
              Send feedback
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}
